// src/components/SystemsList/SystemDetail.tsx
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useSystemExtraDetailsQuery } from "../../features/systems/queries";
import { useAuth } from "../../context/AuthContext";

interface Props {
    systemId: string;
    region: string;
}

export default function SystemDetail({ systemId, region }: Props) {
    const { headersWithAuth } = useAuth();
    const { data, isLoading, isError } = useSystemExtraDetailsQuery(systemId, region, headersWithAuth);

    const formatDateTime = (datetime: string | undefined): string => {
        if (!datetime) return "-";
        return new Date(datetime).toLocaleString();
    };

    const details = [
        { label: "Node.js", key: "node", value: data?.nodeVersion },
        { label: "OS", key: "os-version", value: data?.osVersion },
        { label: "Pusher", key: "pusher", value: data?.pusherVersion },
        { label: "API", key: "api-version", value: data?.apiVersion },
        { label: "Schema", key: "schema-version", value: data?.schemaVersion },
        { label: "App", key: "app-version", value: data?.applicationVersion },
        { label: "Wheels", key: "wheels", value: data?.numberOfWheels },
        { label: "Live Update", key: "live-update", value: formatDateTime(data?.liveViewUpdateTs) },
        { label: "Live Keep Alive", key: "live-keep-alive", value: formatDateTime(data?.liveViewKeepAliveTs) },
        {
            label: "Active Planting Date",
            key: "active-planting-date",
            value: formatDateTime(data?.activePlantingUpdateTimestamp)
        }
    ];

    return (
        <Box
            sx={{
                backgroundColor: "#f9f9f9",
                p: 2,
                borderRadius: 2,
                boxShadow: 1,
                maxWidth: "100%"
            }}
            data-cy={`expandable-row-${systemId}`}
        >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                System Details
            </Typography>
            {isLoading && <Typography>Loading...</Typography>}
            {isError && <Typography color="error">Failed to load system details.</Typography>}
            {!isLoading && !isError && (
                <Table size="small" aria-label="system-details">
                    <TableBody>
                        {details.map(({ key, label, value }) => (
                            <TableRow key={key} data-cy={key}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                    {label}
                                </TableCell>
                                <TableCell>{value || "-"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
